/** Date control conversion is local-time aware and never uses Date's rollover as validation. */
export interface RecordEditorDateField {
  kind: 'date' | 'datetime-local';
  valueFormat?: 'date' | 'iso-start-of-day' | 'iso-end-of-day' | 'local-datetime' | 'iso';
  value?: unknown;
}

type Parts = [number, number, number, number, number, number, number];
const invalid = (): never => { throw new RangeError('Invalid record editor date value'); };
const pad = (value: number, width = 2) => String(value).padStart(width, '0');
function parts(value: string, datetime: boolean): Parts {
  const match = (datetime
    ? /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/
    : /^(\d{4})-(\d{2})-(\d{2})$/).exec(value);
  if (!match) return invalid();
  const result: Parts = [+match[1]!, +match[2]!, +match[3]!, +(match[4] ?? 0), +(match[5] ?? 0), +(match[6] ?? 0), +(match[7] ?? '').padEnd(3, '0')];
  const [year, month, day, hour, minute, second, millisecond] = result;
  const check = new Date(0);
  check.setUTCFullYear(year, month - 1, day);
  check.setUTCHours(hour, minute, second, millisecond);
  if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day || hour > 23 || minute > 59 || second > 59) return invalid();
  return result;
}
function localDate(values: Parts): Date {
  const [year, month, day, hour, minute, second, millisecond] = values;
  const date = new Date(0);
  // setFullYear avoids the constructor's special treatment of years 0 through 99.
  date.setFullYear(year, month - 1, day);
  date.setHours(hour, minute, second, millisecond);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day || date.getHours() !== hour || date.getMinutes() !== minute || date.getSeconds() !== second || date.getMilliseconds() !== millisecond) return invalid();
  return date;
}
function format(field: RecordEditorDateField): string {
  const result = field.valueFormat ?? (field.kind === 'date' ? 'date' : 'local-datetime');
  if (!(field.kind === 'date' ? ['date', 'iso-start-of-day', 'iso-end-of-day'] : ['local-datetime', 'iso']).includes(result)) return invalid();
  return result;
}
export function fromControlValue(field: RecordEditorDateField, value: string): string | null {
  const valueFormat = format(field);
  if (value === '') return null;
  const values = parts(value, field.kind === 'datetime-local');
  if (valueFormat === 'date') return value;
  // Validate local datetime even when retaining the original string: DST gaps are invalid.
  if (valueFormat === 'iso-end-of-day') values.splice(3, 4, 23, 59, 59, 999);
  const date = localDate(values);
  return valueFormat === 'local-datetime' ? value : date.toISOString();
}
export function toControlValue(field: RecordEditorDateField): string {
  const valueFormat = format(field);
  if (field.value === null || field.value === undefined || field.value === '') return '';
  if (typeof field.value !== 'string') return invalid();
  if (valueFormat === 'date' || valueFormat === 'local-datetime') {
    fromControlValue(field, field.value);
    return field.value;
  }
  const match = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?)(Z|[+-]\d{2}:\d{2})$/.exec(field.value);
  if (!match) return invalid();
  parts(match[1]!, true);
  const date = new Date(field.value);
  if (!Number.isFinite(date.getTime()) || date.getFullYear() < 0 || date.getFullYear() > 9999) return invalid();
  const day = `${pad(date.getFullYear(), 4)}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  if (field.kind === 'date') return day;
  return `${day}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${pad(date.getMilliseconds(), 3)}`;
}
